import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Backdrop,
  Box,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export default function ShowCategoriesDialog({
  open,
  onClose,
  categories,
  onSave,
  codeBookLibrary,
  onSaveToLibrary
}) {
  const { t } = useTranslation();

  // Yerel state
  const [localCategories, setLocalCategories] = useState(() =>
    categories.map(cat => ({ ...cat, isNew: false }))
  );
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingCategory, setEditingCategory] = useState(null);

  // Merge diyaloğu state'i
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeIndex, setMergeIndex] = useState(-1);
  const [mergeTargetCode, setMergeTargetCode] = useState("");

  // Birleştirme bilgilerini tutma
  const [mergedCategories, setMergedCategories] = useState([]);

  // "Save to Library" diyaloğu state'i
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [codebookName, setCodebookName] = useState('');
  const [codebookDesc, setCodebookDesc] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // En büyük kodu bulma helper'ı
  const maxCode = useMemo(() => {
    if (localCategories.length === 0) return 0;
    return Math.max(...localCategories.map(cat => parseInt(cat.categoryCode, 10) || 0));
  }, [localCategories]);

  // Yeni kategori ekle
  const handleAddNewCategory = () => {
    const newCode = (maxCode + 1).toString();
    const newCategory = {
      categoryCode: newCode,
      categoryName: "",
      isNew: true
    };
    setLocalCategories(prev => [...prev, newCategory]);
  };

  useEffect(() => {
    if (open) {
      setLocalCategories(categories.map(cat => ({ ...cat, isNew: false })));
      setMergedCategories([]);
      setEditingIndex(-1);
      setEditingCategory(null);
    }
  }, [open, categories]);

  // Yeniden adlandırmaya başla
  const startEditing = (index) => {
    setEditingIndex(index);
    setEditingCategory({ ...localCategories[index] });
  };

  // Düzenleme kaydet
  const handleSaveEdit = () => {
    if (editingIndex !== -1 && editingCategory) {
      setLocalCategories(prev => {
        const newCategories = [...prev];
        newCategories[editingIndex] = editingCategory;
        return newCategories;
      });
      setEditingIndex(-1);
      setEditingCategory(null);
    }
  };

  // Düzenlemeyi iptal et
  const handleCancelEdit = () => {
    setEditingIndex(-1);
    setEditingCategory(null);
  };

  // Merge diyaloğunu aç
  const handleMergeClick = (index) => {
    setMergeIndex(index);
    setMergeDialogOpen(true);
    setMergeTargetCode("");
  };

  // Merge diyaloğunu kapat
  const handleCloseMergeDialog = () => {
    setMergeDialogOpen(false);
    setMergeIndex(-1);
    setMergeTargetCode("");
  };

  // Merge işlemini onayla
  const handleConfirmMerge = () => {
    if (mergeIndex < 0 || !mergeTargetCode) {
      handleCloseMergeDialog();
      return;
    }

    const oldCat = localCategories[mergeIndex];
    const targetCat = localCategories.find(c => c.categoryCode === mergeTargetCode);

    if (!targetCat) {
      handleCloseMergeDialog();
      return;
    }

    const confirmMsg = `${oldCat.categoryName} => ${targetCat.categoryName}\n${t('dialog.mergeConfirm')}`;
    if (!window.confirm(confirmMsg)) {
      handleCloseMergeDialog();
      return;
    }

    setMergedCategories(prev => [
      ...prev,
      {
        oldCatName: oldCat.categoryName,
        oldCatCode: oldCat.categoryCode,
        newCatName: targetCat.categoryName,
        newCatCode: targetCat.categoryCode
      }
    ]);

    setLocalCategories(prev => {
      const newList = [...prev];
      newList.splice(mergeIndex, 1);
      return newList;
    });

    handleCloseMergeDialog();
  };

  // Değişiklikleri kaydet ve diyaloğu kapat
  const handleSave = () => {
    onSave(localCategories, mergedCategories);
    onClose();
  };

  // Yeni ID üretme (örneğin: cb1, cb2, ...)
  const generateNewId = () => {
    const existingIds = codeBookLibrary.codebooks.map(cb => parseInt(cb.id.replace('cb', '')));
    const maxId = Math.max(...existingIds, 0);
    return `cb${maxId + 1}`;
  };

  // Kod kitabına kaydetme işlemi
  const handleSaveToLibrary = async () => {
    setIsSaving(true);
    const newCodebook = {
      id: generateNewId(),
      name: codebookName,
      description: codebookDesc,
      codes: localCategories.map(cat => ({
        code: cat.categoryCode,
        text: cat.categoryName
      }))
    };

    try {
      await onSaveToLibrary(newCodebook);
      setSaveDialogOpen(false);
      setCodebookName('');
      setCodebookDesc('');
    } catch (error) {
      console.error('Error saving to library:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1, mr: 2 }}>
        <DialogTitle>{t('dialog.categoryListTitle') || "Category List"}</DialogTitle>
        <Button variant="outlined" onClick={onClose}>
          {t('dialog.cancel') || "Cancel"}
        </Button>
      </Box>
      <DialogContent dividers>
        {localCategories.length === 0 ? (
          <Box sx={{ textAlign: 'center', padding: '1rem' }}>
            {t('dialog.noCategories') || "No categories available."}
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('dialog.table.code') || "Code"}</TableCell>
                <TableCell>{t('dialog.table.name') || "Name"}</TableCell>
                <TableCell>{t('dialog.table.actions') || "Actions"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {localCategories.map((cat, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    {editingIndex === idx ? (
                      <TextField
                        value={editingCategory.categoryCode}
                        onChange={(e) => setEditingCategory(prev => ({
                          ...prev,
                          categoryCode: e.target.value
                        }))}
                        disabled={!cat.isNew}
                        size="small"
                      />
                    ) : (
                      cat.categoryCode
                    )}
                  </TableCell>
                  <TableCell>
                    {editingIndex === idx ? (
                      <TextField
                        value={editingCategory.categoryName}
                        onChange={(e) => setEditingCategory(prev => ({
                          ...prev,
                          categoryName: e.target.value
                        }))}
                        size="small"
                      />
                    ) : (
                      cat.categoryName
                    )}
                  </TableCell>
                  <TableCell>
                    {editingIndex === idx ? (
                      <>
                        <Button onClick={handleSaveEdit}>
                          {t('dialog.save') || "Save"}
                        </Button>
                        <Button onClick={handleCancelEdit}>
                          {t('dialog.cancel') || "Cancel"}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={() => startEditing(idx)}>
                          {t('dialog.rename') || "Rename"}
                        </Button>
                        <Button onClick={() => handleMergeClick(idx)} sx={{ ml: 1 }}>
                          {t('dialog.merge') || "Merge"}
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Box sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
          <Button variant="contained" onClick={handleAddNewCategory}>
            {t('dialog.addCategory') || "Add Category"}
          </Button>
        </Box>
      </DialogContent>

      {/* Save to Library Diyaloğu */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>{t('dialog.saveToLibraryTitle') || "Save to Library"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('dialog.codebookName') || "Codebook Name"}
            fullWidth
            value={codebookName}
            onChange={(e) => setCodebookName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t('dialog.description') || "Description"}
            fullWidth
            multiline
            value={codebookDesc}
            onChange={(e) => setCodebookDesc(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>
            {t('dialog.cancel') || "Cancel"}
          </Button>
          <Button
            onClick={handleSaveToLibrary}
            color="primary"
            disabled={!codebookName.trim() || isSaving}
          >
            {isSaving ? t('dialog.saving') || "Saving..." : t('dialog.save') || "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <DialogActions>
        <Button 
          onClick={() => setSaveDialogOpen(true)}
          variant="contained" 
          color="secondary"
        >
          {t('dialog.saveToLibrary') || "Save to Library"}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t('dialog.save') || "Save"}
        </Button>
      </DialogActions>

      {/* Merge Diyaloğu */}
      <Dialog open={mergeDialogOpen} onClose={handleCloseMergeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('dialog.mergeTitle') || "Merge Category"}</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small">
            <InputLabel>
              {t('dialog.selectCategory') || "Select Target Category"}
            </InputLabel>
            <Select
              value={mergeTargetCode}
              label={t('dialog.selectCategory') || "Select Target Category"}
              onChange={(e) => setMergeTargetCode(e.target.value)}
            >
              {localCategories.map((c, i) => {
                if (i === mergeIndex) return null;
                return (
                  <MenuItem key={c.categoryCode} value={c.categoryCode}>
                    {c.categoryName} ({c.categoryCode})
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMergeDialog}>
            {t('dialog.cancel') || "Cancel"}
          </Button>
          <Button onClick={handleConfirmMerge} variant="contained" color="primary">
            {t('dialog.confirm') || "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}